<template>
  <dialogue-modal
    v-if="buyerToDelete"
    title="Are you sure you want to send it to recovery?"
    :message="buyerToDelete.company"
    :close-popup="hideDeleteModal"
    :buttons="buttons"
  />
  <page-title pageTitle="Customers" title="All Customers" />

  <div class="row pb-4 gy-3">
    <div class="col-sm-4">
      <router-link to="/customers/new" class="btn btn-primary addtax-modal"
        ><i class="las la-plus me-1"></i> Add New</router-link
      >
    </div>

    <div class="col-sm-auto ms-auto">
      <div class="d-flex gap-3">
        <div class="search-box">
          <input
            type="text"
            class="form-control"
            id="searchMemberList"
            placeholder="Search for Result"
            v-model="searchQuery"
            @input="goToPage(1)"
          />
          <i class="las la-search search-icon"></i>
        </div>
      </div>
    </div>

    <div class="col-sm-auto">
      <div class="d-flex input-group">
        <label class="input-group-text" for="inputGroupSelect01">Show</label>
        <select
          class="form-select"
          id="resultsPerPage"
          v-model="resultsPerPage"
          @change="goToPage(1)"
        >
          <option
            v-for="option in resultsPerPageOptions"
            :key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>
      </div>
    </div>
  </div>

  <div v-if="data.length > 0">
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <table-component
              :headers="headers"
              :data="data"
              :actions="actions"
              :highlightColumn="highlightColumn"
              :offset="(currentPage - 1) * resultsPerPage"
              :recoverable="true"
              @sort="goToPage"
            />
          </div>
        </div>
      </div>
    </div>
    <pagination-component
      :rows-loaded="data.length"
      :current-page="currentPage"
      :num-pages="numPages"
      :total-rows="totalRows"
      :go-to-page="goToPage"
    />
  </div>

  <div v-else class="row">
    <div class="col-xl-12">
      <div class="card">
        <LoadingModal v-if="loading" />
        <div v-else class="card-body">
          {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageTitleComponent from "@/components/shared/PageTitle";
import TableComponent from "@/components/utils/Table";
import { SETTINGS } from "@/constants/config.js";
import { CONSTANTS } from "@/constants/constants.js";
import LoadingModalComponent from "@/components/utils/LoadingModal";
import PaginationComponent from "@/components/utils/paginationComponent";
import DialogueModal from "@/components/utils/modals/DialogueModal";

export default {
  name: "AllBuyersView",
  components: {
    DialogueModal,
    PaginationComponent,
    LoadingModal: LoadingModalComponent,
    PageTitle: PageTitleComponent,
    TableComponent: TableComponent,
  },
  data() {
    return {
      searchQuery: "",
      resultsPerPage: SETTINGS.resultsPerPage,
      buyerToDelete: null,
    };
  },
  computed: {
    headers() {
      return CONSTANTS.BUYER_HEADERS;
    },
    data() {
      return this.$store.getters.allBuyers;
    },
    currentPage() {
      return this.$store.getters.buyersCurrentPage;
    },
    numPages() {
      return this.$store.getters.buyersNumPages;
    },
    totalRows() {
      return this.$store.getters.buyersTotalRows;
    },
    resultsPerPageOptions() {
      const options = CONSTANTS.RESULTS_PER_PAGE_OPTIONS;
      if (!options.includes(this.resultsPerPage)) {
        options.unshift(this.resultsPerPage);
      }
      return options;
    },
    actions() {
      return {
        View: (buyer) => this.viewBuyer(buyer.id),
        Edit: (buyer) => this.editBuyer(buyer.id),
        "Sign Resale Certificate": (buyer) =>
          this.signResaleCertificate(buyer.id),
        "View Resale Certificate": (buyer) =>
          this.viewResaleCertificate(buyer.id),
        Delete: (buyer) => this.showDeleteModal(buyer),
      };
    },
    error() {
      return this.$store.getters.buyersError;
    },
    loading() {
      return this.$store.getters.buyersLoading;
    },
    buttons() {
      return {
        Yes: {
          class: "btn-danger",
          function: () => this.deleteBuyer(),
        },
        No: {
          class: "btn-light px-3",
          function: () => this.hideDeleteModal(),
        },
      };
    },
  },
  methods: {
    async goToPage(page, sortBy = null) {
      let filter = {
        sortingList: sortBy,
        currentPage: page,
        resultsPerPage:
          this.resultsPerPage === "ALL" ? this.totalRows : this.resultsPerPage,
        searchQuery: this.searchQuery,
      };
      await this.$store.dispatch("getBuyers", filter);
    },
    highlightColumn(buyer) {
      const lastVerified = new Date(buyer.dateLastVerified);
      const now = new Date();
      const diffTime = now - lastVerified;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      return {
        "table-warning": diffDays >= 365,
      };
    },
    viewBuyer(id) {
      this.$router.push("/customers/" + id);
    },
    editBuyer(id) {
      this.$router.push("/customers/edit/" + id);
    },
    async signResaleCertificate(id) {
      await this.$store.dispatch("getResaleCertificate", id);
    },
    viewResaleCertificate(id) {
      const buyer = this.data.find((b) => b.id === id);
      if (!buyer || !buyer.company) {
        console.error("Buyer not found or company missing");
        return;
      }

      if (!buyer.dateLastVerified) {
        alert("No Resale Certificate Uploaded");
        return;
      }

      const filename = `Resale_Certificate_${buyer.id}.jpg`;
      const fileUrl = `/files/resaleCertificates/${filename}`;

      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    showDeleteModal(buyer) {
      this.buyerToDelete = buyer;
    },
    async deleteBuyer() {
      if (this.buyerToDelete) {
        await this.$store.dispatch("deleteBuyer", this.buyerToDelete.id);
        await this.goToPage(1);
        this.hideDeleteModal();
      } else {
        alert("There has been an error");
      }
    },
    hideDeleteModal() {
      this.buyerToDelete = null;
    },
  },
  async created() {
    let filter = {
      resultsPerPage: SETTINGS.resultsPerPage,
    };
    await this.$store.dispatch("getBuyers", filter);
  },
  async beforeUnmount() {
    await this.$store.dispatch("resetCustomer");
  },
};
</script>

<style scoped></style>
